/**
 * Schema.org WebSite JSON-LD — once per tenant on the home/landing.
 *
 * <p>Two SEO payoffs:
 * <ul>
 *   <li>SiteLinks Search Box eligibility (when {@code searchUrlTemplate}
 *       is set) — Google can render an inline search box under the
 *       tenant's SERP listing.</li>
 *   <li>Brand-name disambiguation — establishes "this URL = this
 *       name" so Knowledge Graph anchors the brand to the right URL.</li>
 * </ul>
 */
import { JsonLdScript } from './_shared';

export interface WebSiteSchemaJsonLdProps {
  name: string;
  alternateName?: string;
  url: string;
  /** Search endpoint pattern with a {@code {search_term_string}} token
   *  for the query. Example:
   *  {@code "${origin}/${tenant}/search?q={search_term_string}"}. */
  searchUrlTemplate?: string;
  description?: string;
  id?: string;
}

export function WebSiteSchemaJsonLd({ id, ...props }: WebSiteSchemaJsonLdProps) {
  const schema = buildWebSiteSchema(props);
  return <JsonLdScript schema={schema} id={id ?? 'schema-website'} />;
}

export function buildWebSiteSchema(input: Omit<WebSiteSchemaJsonLdProps, 'id'>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: input.name,
    alternateName: input.alternateName,
    url: input.url,
    description: input.description,
    potentialAction: input.searchUrlTemplate
      ? {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: input.searchUrlTemplate,
          },
          'query-input': 'required name=search_term_string',
        }
      : undefined,
  };
}
